
import React, { useRef, useState } from "react";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";

import QualityCheckForm from "./RawMaterialQualityCheck";
import { RawMaterialPurchaseListDTO } from "../../models/RawMaterials/RawMaterial-model";

interface DataTableProps {
  data: RawMaterialPurchaseListDTO[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: any) => void;
  isEmptyState?: boolean;
  onUpdate: (material: RawMaterialPurchaseListDTO) => void;
  onSortChange: (field: string, order: 1 | -1) => void;
  qcStatuses?: any;
  onRefresh?: () => void;
  sortField?: string;
  sortOrder?: 1 | -1 | null;
  hideQcColumns?: boolean;
}

const RawMaterialPurchaseViewTable: React.FC<DataTableProps> = (props) => {
  const sortField = props.sortField;
  const sortOrder = props.sortOrder;
  const toast = useRef<Toast>(null);

  const [showQualityCheckForm, setShowQualityCheckForm] = useState(false);
  const [selectedRawMaterial, setSelectedRawMaterial] = useState<any | null>(
    null
  );

  const showMessage = (
    severity: "success" | "error" | "warn",
    summary: string,
    detail: string
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const onSort = (e: DataTableSortEvent) => {
    const field = e.sortField as string;
    const order = (e.sortOrder as 1 | -1 | 0 | null | undefined) || 0;

    if (!field || !order) return;

    const mappedOrder: 1 | -1 = order === 1 ? 1 : -1;
    props.onSortChange(field, mappedOrder);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const rupee = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });

  const parseNumber = (val: any): number => {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const n = parseFloat(val.replace(/[^0-9.-]/g, ""));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  };

  const getFirstNumericValue = (rowData: any, keys: string[]) => {
    for (const key of keys) {
      const rawValue = rowData?.[key];
      if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
        return parseNumber(rawValue);
      }
    }
    return null;
  };

  const approxEqual = (a: number, b: number, eps = 1e-6) =>
    Math.abs(a - b) < eps;

  const getUnitPrice = (rowData: any): number => {
    const explicitUnitPrice = getFirstNumericValue(rowData, [
      "unitPrice",
      "UnitPrice",
      "invoiceUnitPrice",
      "InvoiceUnitPrice",
    ]);
    if (explicitUnitPrice !== null) return explicitUnitPrice;

    const invoicedQuantity =
      getFirstNumericValue(rowData, [
        "quantity",
        "quantityWithUnit",
        "invoicedQuantity",
        "Quantity",
        "InvoicedQuantity",
      ]) ?? 0;

    const baseAmount =
      getFirstNumericValue(rowData, ["basicAmount", "BasicAmount", "amount", "Amount"]) ?? 0;

    if (!invoicedQuantity) return 0;
    return baseAmount / invoicedQuantity;
  };

  const getQCStatus = (rowData: any): string => {
    const invoiced =
      parseNumber(rowData.quantity) ??
      parseNumber(rowData.quantityWithUnit) ??
      0;
    const accepted =
      parseNumber(rowData.acceptedQuantity) ??
      parseNumber(rowData.acceptedQuantityWithUnit) ??
      0;
    const rejected =
      parseNumber(rowData.rejectedQuantity) ??
      parseNumber(rowData.rejectedQuantityWithUnit) ??
      0;
    const remainingFromApi =
      parseNumber(rowData.remainingQuantity) ??
      parseNumber(rowData.remainingQuantityWithUnit) ??
      0;

    const sumAR = accepted + rejected;
    const computedRemaining = Math.max(0, invoiced - sumAR);
    const remaining = remainingFromApi || computedRemaining;

    let statusName = "QC Pending";

    if (approxEqual(invoiced, accepted) && invoiced > 0) {
      statusName = "Accepted";
    } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
      statusName = "Rejected";
    } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
      statusName = "Accepted & Rejected";
    } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
      statusName = "QC Pending";
    } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
      statusName = "Partially Rejected";
    } else {
      statusName = "Partially Accepted";
    }

    return statusName;
  };

  const qualityCheckButtonTemplate = (rowData: any) => {
    const invoiced =
      parseNumber(rowData.quantity) ??
      parseNumber(rowData.quantityWithUnit) ??
      0;
    const accepted =
      parseNumber(rowData.acceptedQuantity) ??
      parseNumber(rowData.acceptedQuantityWithUnit) ??
      0;
    const rejected =
      parseNumber(rowData.rejectedQuantity) ??
      parseNumber(rowData.rejectedQuantityWithUnit) ??
      0;
    const remainingFromApi =
      parseNumber(rowData.remainingQuantity) ??
      parseNumber(rowData.remainingQuantityWithUnit) ??
      0;

    const sumAR = accepted + rejected;
    const computedRemaining = Math.max(0, invoiced - sumAR);
    const remaining = remainingFromApi || computedRemaining;

    let statusName = "QC Pending";
    let className = "p-button-warning p-button-sm";
    let disabled = false;

    if (approxEqual(invoiced, accepted) && invoiced > 0) {
      statusName = "Accepted";
      className = "p-button-success p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, rejected) && invoiced > 0) {
      statusName = "Rejected";
      className = "p-button-danger p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, sumAR) && invoiced > 0) {
      statusName = "Accepted & Rejected";
      className = "p-button-help p-button-sm";
      disabled = true;
    } else if (approxEqual(invoiced, remaining) && invoiced > 0) {
      statusName = "QC Pending";
      className = "p-button-warning p-button-sm";
      disabled = false;
    } else if (approxEqual(accepted, 0) && rejected > 0 && !approxEqual(rejected, invoiced)) {
      statusName = "Partially Rejected";
      className = "p-button-danger p-button-sm";
      disabled = false;
    } else {
      statusName = "Partially Accepted";
      className = "p-button-info p-button-sm";
      disabled = false;
    }

    const qcStatus = props.qcStatuses?.find((s: any) =>
      s.name?.toLowerCase().trim() === statusName.toLowerCase().trim()
    );
    const finalLabel = qcStatus?.name || statusName;

    const handleClick = () => {
      if (disabled) return;

      setSelectedRawMaterial({
        ...rowData,
        invoicedQuantity: invoiced,
        acceptedQuantityNum: accepted,
        rejectedQuantityNum: rejected,
        remainingQuantity: remaining,
        qcStatusName: finalLabel,
      });
      setShowQualityCheckForm(true);
    };

    return (
      <Button
        label={finalLabel}
        className={className}
        disabled={disabled}
        onClick={handleClick}
        style={{
          fontSize: "0.75rem",
          padding: "4px 8px",
          height: "32px",
          width: "160px",
          color: "#fff",
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />
    );
  };

  const qcByBodyTemplate = (rowData: any) => {
    const status = getQCStatus(rowData);
    if (status === "QC Pending") return "-";
    return rowData.updatedByName || rowData.qcByName || "-";
  };

  const qcDateBodyTemplate = (rowData: any) => {
    const status = getQCStatus(rowData);
    if (status === "QC Pending") return "-";
    const dateToFormat = rowData.updatedDate || rowData.qcDate;
    return dateToFormat ? formatDate(dateToFormat) : "-";
  };

  const invoiceNumberBody = (rowData: any) =>
    rowData.invoiceNumber || rowData.invoiceId || "-";

  const handleQualityCheckSave = async (updatedMaterial: any) => {
    try {
      const materialToUpdate = {
        ...updatedMaterial,
        qcStatusName: updatedMaterial.qcStatusName,
        updatedByName: updatedMaterial.updatedByName || updatedMaterial.qcByName,
        updatedDate:
          updatedMaterial.updatedDate ||
          updatedMaterial.qcDate ||
          new Date().toISOString(),
      };

      setShowQualityCheckForm(false);
      showMessage("success", "Success", "Quality check completed successfully");
      props.onUpdate(materialToUpdate as RawMaterialPurchaseListDTO);

      if (props.onRefresh) {
        setTimeout(() => {
          props.onRefresh?.();
        }, 800);
      }
    } catch (error) {
      console.error("Error in handleQualityCheckSave", error);
      showMessage("error", "Error", "Failed to update quality check");
    }
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={props.data}
          paginator
          rows={props.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          totalRecords={props.totalRecords}
          lazy
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate={
            props.isEmptyState
              ? `totalRecords materials`
              : `{first} to {last} of {totalRecords}`
          }
          first={(props.pageNumber - 1) * props.pageSize}
          onPage={props.onPageChange}
          sortMode="single"
          sortField={sortField}
          sortOrder={sortOrder ?? 0}
          onSort={onSort}
          editMode="row"
        >
          <Column
            field="purchaseDate"
            header="Purchase Date"
            style={{ width: "10%" }}
            sortable
            body={(rowData) => formatDate(rowData.purchaseDate)}
          />
          {!props.hideQcColumns && (
            <Column
              header="Actions"
              body={qualityCheckButtonTemplate}
              style={{ textAlign: "center", width: "140px" }}
            />
          )}
          <Column
            field="rawMaterialName"
            header="Raw Material"
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="categoryName"
            header="Category"
            style={{ width: "15%" }}
            sortable
          />
          <Column
            field="invoiceNumber"
            header="Invoice No."
            body={invoiceNumberBody}
            style={{ width: "12%" }}
            sortable
          />
          <Column
            field="vendorName"
            header="Vendor"
            style={{ width: "15%" }}
            sortable
          />
          <Column
            field="quantity"
            header="Invoiced Qty"
            body={(rowData) => rowData.quantityWithUnit || rowData.quantity}
            style={{ width: "10%" }}
            sortable
          />
          <Column
            field="posAlias"
            header="Unit"
            style={{ width: "12%" }}
            sortable
          />
          {!props.hideQcColumns && (
            <>
              <Column
                field="acceptedQuantity"
                header="Accepted Qty"
                body={(rowData) =>
                  rowData.acceptedQuantityWithUnit || rowData.acceptedQuantity
                }
                style={{ width: "10%" }}
                sortable
              />
              <Column
                field="remainingQuantity"
                header="Remaining Qty"
                body={(rowData) =>
                  rowData.remainingQuantityWithUnit || rowData.remainingQuantity
                }
                style={{ width: "10%" }}
                sortable
              />
              <Column
                field="rejectedQuantity"
                header="Rejected Qty"
                body={(rowData) =>
                  rowData.rejectedQuantityWithUnit || rowData.rejectedQuantity
                }
                style={{ width: "10%" }}
                sortable
              />
            </>
          )}
          <Column
            field="basicAmount"
            header="Base Price"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.basicAmount))}
            sortable
          />
          <Column
            field="unitPrice"
            header="Unit Price"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(getUnitPrice(rowData))}
            sortable
          />
          <Column
            field="taxAmount"
            header="Tax Amount"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.taxAmount))}
            sortable
          />
          <Column
            field="totalAmount"
            header="Total Amount"
            style={{ width: "10%" }}
            body={(rowData) => rupee.format(parseNumber(rowData.totalAmount))}
            sortable
          />
          <Column
            header="Purchased By"
            body={(rowData: any) => rowData.purchasedBy || rowData.createdByName || rowData.createdbyName || '-'}
            style={{ width: "12%" }}
            sortable
          />
          {!props.hideQcColumns && (
            <>
              <Column
                field="updatedByName"
                header="QC By"
                style={{ width: "10%" }}
                sortable
                body={qcByBodyTemplate}
              />
              <Column
                field="updatedDate"
                header="QC Date"
                body={qcDateBodyTemplate}
                style={{ width: "10%" }}
                sortable
              />
            </>
          )}
        </DataTable>

        {!props.hideQcColumns && (
          <QualityCheckForm
            visible={showQualityCheckForm}
            onHide={() => setShowQualityCheckForm(false)}
            rawMaterial={selectedRawMaterial}
            onSaveSuccess={handleQualityCheckSave}
          />
        )}
      </div>
    </div>
  );
};

export default RawMaterialPurchaseViewTable;
